
import { useMsal } from '@azure/msal-react';
import { useCobaltLayoutContext } from '@cae/cobalt-react/components/layout';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { PrecheckinRouterProps } from './PrecheckinRouter.types';

/**
 * Precheckin - MFE wrapper for Precheckin page
 *
 * This feature loads the remote Precheckin MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in Train module main page):
 * 1. User must be logged in
 * 2. FeatureFlagGuard - precheckinMfeEnabled must be enabled
 * 3. PermissionGuard - ModuleTrain_View_Any required
 */
const PrecheckinRouterPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const { trainingId } = useParams<{ trainingId: string }>();
  const navigate = useNavigate();
  const { hideSideNavigation } = useCobaltLayoutContext();
  const getToken = useGetToken();

  const onNavigateHome = React.useCallback((): void => {
    navigate(APP_ROUTES.TRAIN_OVERVIEW);
  }, [navigate]);

  const [RemoteComponent, setRemoteComponent] =
    React.useState<React.ComponentType<PrecheckinRouterProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      try {
        const { default: Router } =
          await import('access_pass_mfe/Precheckin');
        setRemoteComponent(() => Router);
      } catch (error) {
        console.error('Error loading PrecheckinRouter:', error);
        setLoadError(error as Error);
      }
    };

    loadRouter();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteComponent) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteComponent
        msalInstance={instance}
        user={authData}
        permissions={permissions}
        trainingId={trainingId}
        onNavigateHome={onNavigateHome}
        hideSideNavigation={hideSideNavigation}
        getToken={getToken}
      />
    </React.Suspense>
  );
});

PrecheckinRouterPage.displayName = 'PrecheckinRouterPage';

export { PrecheckinRouterPage };

